import _EnterpriseOpsService from './EnterpriseOpsService';

// Sprint 2.4 (Branding Foundation): the app already renders entirely from CSS custom properties
// (--primary-color, --secondary-color, etc., defined in assets/scss/themes.scss). This module lets an
// admin's values for those same tokens (set via EnterpriseConfigController.Update, Branding category)
// apply at runtime — no multi-tenancy, one global brand config for the instance, as scoped.
const BRANDING_CACHE_KEY = 'ENT_BRANDING_CONFIG';

let _branding = {
  async applyBranding() {
    try {
      const response = await _EnterpriseOpsService.getBrandingConfig();
      if (response && response.status === 200 && response.data) {
        localStorage.setItem(BRANDING_CACHE_KEY, JSON.stringify(response.data));
        this.applyValues(response.data);
      }
    } catch (e) {
      // Branding is cosmetic only — never block app startup on a failed/anonymous-blocked fetch.
      console.log('Branding fetch skipped: ', e);
    }
  },

  applyValues(branding) {
    const root = document.documentElement;
    if (branding.PrimaryColor) root.style.setProperty('--primary-color', branding.PrimaryColor);
    if (branding.SecondaryColor) root.style.setProperty('--secondary-color', branding.SecondaryColor);
    // Sprint 2.6.7A (Candidate Homepage Modernization, §8): Header/Button/Link/Footer color tokens —
    // each CSS var already aliases --primary-color/--secondary-color by default in themes.scss :root,
    // so leaving any of these unset (an admin who hasn't configured them yet) degrades gracefully to
    // exactly today's look rather than an unstyled/missing color.
    if (branding.HeaderColor) root.style.setProperty('--header-color', branding.HeaderColor);
    if (branding.ButtonColor) root.style.setProperty('--button-color', branding.ButtonColor);
    if (branding.ButtonTextColor) root.style.setProperty('--button-text-color', branding.ButtonTextColor);
    if (branding.LinkColor) root.style.setProperty('--link-color', branding.LinkColor);
    if (branding.FooterColor) root.style.setProperty('--footer-color', branding.FooterColor);
  },

  getCached() {
    try {
      const cached = localStorage.getItem(BRANDING_CACHE_KEY);
      return cached ? JSON.parse(cached) : {};
    } catch (e) {
      return {};
    }
  },

  getOrganizationName(defaultValue) {
    const branding = this.getCached();
    return branding.OrganizationName || defaultValue;
  },

  getLogoUrl(defaultValue) {
    const branding = this.getCached();
    return branding.LogoUrl || defaultValue;
  },

  getFooterText(defaultValue) {
    const branding = this.getCached();
    return branding.FooterText || defaultValue;
  },

  // Phase C.7.2 (Candidate Experience Design System, Branding Foundation): the single authoritative
  // logo-resolution path for every candidate-facing surface. C.7.1 discovery found 3 different
  // mechanisms in live use — the homepage's own inline `resData.Portal_Logo || _branding.getLogoUrl(
  // PROJECT_LOGO)` (correct, Phase C.6.2.1), PrivateHeader.jsx's raw `localStorage['Portal_Logo']`
  // read with NO fallback at all, and 5 screens (RegistrationMessage, Preview, Receipt, Admit Card,
  // Interview Call Letter) rendering the env-configured PROJECT_LOGO constant directly, bypassing
  // admin-configured branding entirely. This function is that one priority chain, extracted so every
  // consumer calls the same logic instead of re-implementing (or omitting) it: the tenant's own
  // uploaded PortalConfiguration.Portal_Logo (Admin-editable via Portal Config, cached to localStorage
  // by AuthService.cachePortalConfiguration()) first, falling back to this module's own
  // EnterpriseConfiguration/Branding LogoUrl, falling back to the caller-supplied default (typically
  // PROJECT_LOGO) last. No new data-fetching/caching behavior is introduced — this only unifies which
  // already-cached values are consulted and in what order.
  resolveLogoUrl(portalConfigLogo, defaultValue) {
    return portalConfigLogo || this.getLogoUrl(defaultValue);
  },

  // Companion for organization/recruitment name — a thin wrapper (not new data) so every consumer
  // makes the same one call rather than each choosing its own fallback string independently (C.7.1
  // found PublicHeader and the homepage already used two different fallback defaults — that
  // divergence is a caller choice, intentionally still allowed via the `defaultValue` param, but the
  // resolution mechanism itself is now the same named function everywhere).
  resolveOrganizationName(defaultValue) {
    return this.getOrganizationName(defaultValue);
  }
};

export default _branding;
